import type {
  CoreAdminProps,
  AuthProvider,
  DashboardComponent,
  LayoutComponent,
} from "ra-core";
import { CustomRoutes, localStorageStore, Resource } from "ra-core";
import { useEffect, useMemo } from "react";
import { Route } from "react-router";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { Admin } from "@/components/admin/admin";
import { ForgotPasswordPage } from "@/components/supabase/forgot-password-page";
import { SetPasswordPage } from "@/components/supabase/set-password-page";
import { OAuthConsentPage } from "@/components/supabase/oauth-consent-page";

import companies from "../companies";
import contacts from "../contacts";
import { Dashboard } from "../dashboard/Dashboard";
import { MobileDashboard } from "../dashboard/MobileDashboard";
import deals from "../deals";
import estimates from "../estimates";
import invoices from "../invoices";
import jobs from "../jobs";
import payments from "../payments";
import pricebook from "../pricebook";
import { Layout } from "../layout/Layout";
import { MobileLayout } from "../layout/MobileLayout";
import { SignupPage } from "../login/SignupPage";
import { ConfirmationRequired } from "../login/ConfirmationRequired";
import { ImportPage } from "../misc/ImportPage";
import { ChangelogPage } from "../misc/ChangelogPage";
import {
  getAuthProvider as defaultAuthProviderBuilder,
  getDataProvider as defaultDataProviderBuilder,
} from "../providers/supabase";
import sales from "../sales";
import { SettingsPageMobile } from "../settings/SettingsPageMobile";
import { ProfilePage } from "../settings/ProfilePage";
import { SettingsPage } from "../settings/SettingsPage";
import {
  CONFIGURATION_STORE_KEY,
  type ConfigurationContextValue,
} from "./ConfigurationContext";
import type { CrmDataProvider } from "../providers/types";
import {
  defaultCompanySectors,
  defaultCurrency,
  defaultDarkModeLogo,
  defaultDealCategories,
  defaultDealPipelineStatuses,
  defaultDealStages,
  defaultLightModeLogo,
  defaultNoteStatuses,
  defaultTaskTypes,
  defaultTitle,
} from "./defaultConfiguration";
import { i18nProvider as defaulti18nProvider } from "../providers/commons/i18nProvider";
import { ComingSoon } from "../../gravun/layout/ComingSoon";
import { UNIMPLEMENTED_NAV_ITEMS } from "../../gravun/navigation/navConfig";
import { CalendarPage } from "../../gravun/calendar/CalendarPage";
import { SequencesPage } from "../../gravun/sequences/SequencesPage";
import { InboxPage } from "../../gravun/inbox/InboxPage";
import { PublicBookingPage } from "../../gravun/calendar/PublicBookingPage";
import { CampaignsPage } from "../../gravun/campaigns/CampaignsPage";
import { WorkflowsPage } from "../../gravun/workflows/WorkflowsPage";
import { FormsPage } from "../../gravun/forms/FormsPage";
import { ExpensesPage } from "../../gravun/expenses/ExpensesPage";
import { ChatWidgetPage } from "../../gravun/chat/ChatWidgetPage";
import { SaasSignupPage } from "../../gravun/signup/SaasSignupPage";
import { WelcomePage } from "../../gravun/onboarding/WelcomePage";
import { ConnectionsPage } from "../../gravun/connections/ConnectionsPage";
import { PhonePage } from "../../gravun/phone/PhonePage";
import { ReportsPage } from "../../gravun/reports/ReportsPage";
import { ReviewsPage } from "../../gravun/reviews/ReviewsPage";
import { PublicFormPage } from "../../gravun/forms/PublicFormPage";
import { PublicEstimatePage } from "../../gravun/estimates/PublicEstimatePage";
import { TasksPage } from "../../gravun/tasks/TasksPage";
import { TeamPage } from "../../gravun/team/TeamPage";
import { StartPage } from "../login/StartPage.tsx";
import { useIsMobile } from "@/hooks/use-mobile.ts";
import { MobileTasksList } from "../tasks/MobileTasksList.tsx";
import { ContactListMobile } from "../contacts/ContactList.tsx";
import { ContactShow } from "../contacts/ContactShow.tsx";
import { CompanyShow } from "../companies/CompanyShow.tsx";
import { NoteShowPage } from "../notes/NoteShowPage.tsx";

const defaultStore = localStorageStore(undefined, "CRM");

export type CRMProps = {
  dataProvider?: CrmDataProvider;
  authProvider?: AuthProvider;
  i18nProvider?: CoreAdminProps["i18nProvider"];
  disableTelemetry?: boolean;
  store?: CoreAdminProps["store"];
  dashboard?: DashboardComponent;
  layout?: LayoutComponent;
} & Partial<ConfigurationContextValue>;

/**
 * CRM Component
 *
 * This component sets up and renders the main CRM application using `ra-core`. It provides
 * default configurations and themes but allows for customization through props. The component
 * seeds the store with any custom prop values for backwards compatibility.
 *
 * @param {LabeledValue[]} companySectors - The list of company sectors used in the application.
 * @param {string} currency - The ISO 4217 currency code used to format monetary values (e.g. "USD", "EUR", "GBP").
 * @param {RaThemeOptions} darkTheme - The theme to use when the application is in dark mode.
 * @param {LabeledValue[]} dealCategories - The categories of deals used in the application.
 * @param {string[]} dealPipelineStatuses - The statuses of deals in the pipeline used in the application.
 * @param {DealStage[]} dealStages - The stages of deals used in the application.
 * @param {RaThemeOptions} lightTheme - The theme to use when the application is in light mode.
 * @param {string} darkModeLogo - Logo shown in dark mode and on the auth pages. Must be an imported asset, an absolute URL, or a data URI, never a route-relative path like "./logos/x.svg", which breaks on nested routes such as /oauth/consent (issue #291).
 * @param {string} lightModeLogo - Logo shown in light mode. Same rule as darkModeLogo: imported asset, absolute URL, or data URI only.
 * @param {NoteStatus[]} noteStatuses - The statuses of notes used in the application.
 * @param {LabeledValue[]} taskTypes - The types of tasks used in the application.
 * @param {string} title - The title of the CRM application.
 *
 * @returns {JSX.Element} The rendered CRM application.
 *
 * @example
 * // Basic usage of the CRM component
 * import { CRM } from '@/components/atomic-crm/dashboard/CRM';
 *
 * const App = () => (
 *     <CRM
 *         darkModeLogo="https://example.com/logo-dark.svg"
 *         lightModeLogo="https://example.com/logo-light.svg"
 *         title="My Custom CRM"
 *         lightTheme={{
 *             ...defaultTheme,
 *             palette: {
 *                 primary: { main: '#0000ff' },
 *             },
 *         }}
 *     />
 * );
 *
 * export default App;
 */
export const CRM = ({
  companySectors = defaultCompanySectors,
  currency = defaultCurrency,
  dealCategories = defaultDealCategories,
  dealPipelineStatuses = defaultDealPipelineStatuses,
  dealStages = defaultDealStages,
  darkModeLogo = defaultDarkModeLogo,
  lightModeLogo = defaultLightModeLogo,
  noteStatuses = defaultNoteStatuses,
  taskTypes = defaultTaskTypes,
  title = defaultTitle,
  dataProvider = defaultDataProviderBuilder(),
  authProvider = defaultAuthProviderBuilder(),
  i18nProvider = defaulti18nProvider,
  store = defaultStore,
  disableTelemetry,
  ...rest
}: CRMProps) => {
  useEffect(() => {
    if (
      disableTelemetry ||
      process.env.NODE_ENV !== "production" ||
      typeof window === "undefined" ||
      typeof window.location === "undefined" ||
      typeof Image === "undefined"
    ) {
      return;
    }
    const img = new Image();
    img.src = `https://atomic-crm-telemetry.marmelab.com/atomic-crm-telemetry?domain=${window.location.hostname}`;
  }, [disableTelemetry]);

  // Seed the store with CRM prop values if not already stored
  // (backwards compatibility for prop-based config)
  useEffect(() => {
    if (!store.getItem(CONFIGURATION_STORE_KEY)) {
      store.setItem(CONFIGURATION_STORE_KEY, {
        companySectors,
        currency,
        dealCategories,
        dealPipelineStatuses,
        dealStages,
        noteStatuses,
        taskTypes,
        title,
        darkModeLogo,
        lightModeLogo,
      } satisfies ConfigurationContextValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  const isMobile = useIsMobile();

  // on login, pre-fetch the configuration to avoid a flickering
  // when accessing the app for the first time
  const wrappedAuthProvider = useMemo<AuthProvider>(
    () => ({
      ...authProvider,
      login: async (params: any) => {
        const result = await authProvider.login(params);
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            store.setItem(CONFIGURATION_STORE_KEY, config);
          }
        } catch {
          // Non-critical: config will load via useConfigurationLoader
        }
        return result;
      },
      handleCallback: async (params: any) => {
        if (!authProvider.handleCallback) {
          throw new Error(
            "handleCallback is not implemented in the authProvider",
          );
        }
        const result = await authProvider.handleCallback(params);
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            store.setItem(CONFIGURATION_STORE_KEY, config);
          }
        } catch {
          // Non-critical: config will load via useConfigurationLoader
        }
        return result;
      },
      logout: async (params: any) => {
        try {
          store.removeItem(CONFIGURATION_STORE_KEY);
        } catch {
          // Ignore
        }
        return authProvider.logout(params);
      },
    }),
    [authProvider, dataProvider, store],
  );

  const ResponsiveAdmin = isMobile ? MobileAdmin : DesktopAdmin;

  return (
    <ResponsiveAdmin
      dataProvider={dataProvider}
      authProvider={wrappedAuthProvider}
      i18nProvider={i18nProvider}
      store={store}
      loginPage={StartPage}
      requireAuth
      disableTelemetry
      {...rest}
    />
  );
};

const DesktopAdmin = (
  props: CoreAdminProps & {
    dashboard?: DashboardComponent;
    layout?: LayoutComponent;
  },
) => {
  return (
    <Admin
      layout={props.layout ?? Layout}
      dashboard={props.dashboard ?? Dashboard}
      {...props}
    >
      <CustomRoutes noLayout>
        <Route path={SignupPage.path} element={<SignupPage />} />
        <Route
          path={ConfirmationRequired.path}
          element={<ConfirmationRequired />}
        />
        <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
        <Route
          path={ForgotPasswordPage.path}
          element={<ForgotPasswordPage />}
        />
        <Route path={OAuthConsentPage.path} element={<OAuthConsentPage />} />
        <Route path={PublicBookingPage.path} element={<PublicBookingPage />} />
        <Route path={PublicFormPage.path} element={<PublicFormPage />} />
        <Route
          path={PublicEstimatePage.path}
          element={<PublicEstimatePage />}
        />
        <Route path={ChatWidgetPage.path} element={<ChatWidgetPage />} />
        <Route path={SaasSignupPage.path} element={<SaasSignupPage />} />
      </CustomRoutes>

      <CustomRoutes>
        <Route path={ProfilePage.path} element={<ProfilePage />} />
        <Route path={SettingsPage.path} element={<SettingsPage />} />
        <Route path={ImportPage.path} element={<ImportPage />} />
        <Route path={ChangelogPage.path} element={<ChangelogPage />} />
        <Route path="/tasks" element={<TasksPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/sequences" element={<SequencesPage />} />
        <Route path="/forms" element={<FormsPage />} />
        <Route path="/expenses" element={<ExpensesPage />} />
        <Route path={WelcomePage.path} element={<WelcomePage />} />
        <Route path="/connections" element={<ConnectionsPage />} />
        <Route path="/phone" element={<PhonePage />} />
        <Route path="/campaigns" element={<CampaignsPage />} />
        <Route path="/workflows" element={<WorkflowsPage />} />
        <Route path="/reviews" element={<ReviewsPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/inbox" element={<InboxPage />} />
        <Route path="/team" element={<TeamPage />} />
        {UNIMPLEMENTED_NAV_ITEMS.map((item) => (
          <Route key={item.key} path={item.to} element={<ComingSoon />} />
        ))}
      </CustomRoutes>
      <Resource name="deals" {...deals} />
      <Resource name="contacts" {...contacts} />
      <Resource name="companies" {...companies} />
      <Resource name="jobs" {...jobs} />
      <Resource name="products" {...pricebook} />
      <Resource name="estimates" {...estimates} />
      <Resource name="estimate_line_items" />
      <Resource name="estimate_templates" />
      <Resource name="estimate_template_items" />
      <Resource name="invoices" {...invoices} />
      <Resource name="invoice_line_items" />
      <Resource name="payments" {...payments} />
      <Resource name="appointments" />
      <Resource name="booking_pages" />
      <Resource name="sequences" />
      <Resource name="sequence_steps" />
      <Resource name="sequence_enrollments" />
      <Resource name="forms" />
      <Resource name="form_submissions" />
      <Resource name="review_requests" />
      <Resource name="segments" />
      <Resource name="campaigns" />
      <Resource name="campaign_recipients" />
      <Resource name="workflows" />
      <Resource name="workflow_actions" />
      <Resource name="workflow_runs" />
      <Resource name="workflow_run_steps" />
      <Resource name="calls" />
      <Resource name="comments" />
      <Resource name="record_files" />
      <Resource name="notifications" />
      <Resource name="message_templates" />
      <Resource name="contact_timeline" />
      <Resource name="task_checklist_items" />
      <Resource name="checklist_templates" />
      <Resource name="pipelines" />
      <Resource name="pipeline_stages" />
      <Resource name="workspaces" />
      <Resource name="conversations" />
      <Resource name="messages" />
      <Resource name="integration_connections" />
      <Resource name="contact_notes" />
      <Resource name="deal_notes" />
      <Resource name="tasks" />
      <Resource name="sales" {...sales} />
      <Resource name="tags" />
    </Admin>
  );
};

const MobileAdmin = (
  props: CoreAdminProps & {
    dashboard?: DashboardComponent;
    layout?: LayoutComponent;
  },
) => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        gcTime: 1000 * 60 * 60 * 24, // 24 hours
        networkMode: "offlineFirst",
      },
      mutations: {
        networkMode: "offlineFirst",
      },
    },
  });
  const asyncStoragePersister = createAsyncStoragePersister({
    storage: localStorage,
  });

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister: asyncStoragePersister }}
    >
      <Admin
        queryClient={queryClient}
        layout={props.layout ?? MobileLayout}
        dashboard={props.dashboard ?? MobileDashboard}
        {...props}
      >
        <CustomRoutes noLayout>
          <Route path={SignupPage.path} element={<SignupPage />} />
          <Route
            path={ConfirmationRequired.path}
            element={<ConfirmationRequired />}
          />
          <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
          <Route
            path={ForgotPasswordPage.path}
            element={<ForgotPasswordPage />}
          />
          <Route path={OAuthConsentPage.path} element={<OAuthConsentPage />} />
        </CustomRoutes>
        <CustomRoutes>
          <Route
            path={SettingsPageMobile.path}
            element={<SettingsPageMobile />}
          />
          <Route path={ChangelogPage.path} element={<ChangelogPage />} />
          {/* Field staff need the whole product on a phone, not a subset:
              without these the Inbox, jobs, billing and automation modules
              404 on mobile even though the sidebar links to them. */}
          <Route path="/inbox" element={<InboxPage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/sequences" element={<SequencesPage />} />
          <Route path="/campaigns" element={<CampaignsPage />} />
          <Route path="/workflows" element={<WorkflowsPage />} />
          <Route path="/forms" element={<FormsPage />} />
          <Route path="/expenses" element={<ExpensesPage />} />
          <Route path={WelcomePage.path} element={<WelcomePage />} />
          <Route path="/connections" element={<ConnectionsPage />} />
          <Route path="/phone" element={<PhonePage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/team" element={<TeamPage />} />
        </CustomRoutes>
        <Resource
          name="contacts"
          list={ContactListMobile}
          show={ContactShow}
          recordRepresentation={contacts.recordRepresentation}
        >
          <Route path=":id/notes/:noteId" element={<NoteShowPage />} />
        </Resource>
        <Resource name="companies" show={CompanyShow} />
        <Resource name="tasks" list={MobileTasksList} />
        <Resource name="deals" {...deals} />
        <Resource name="jobs" {...jobs} />
        <Resource name="estimates" {...estimates} />
        <Resource name="estimate_line_items" />
        <Resource name="invoices" {...invoices} />
        <Resource name="invoice_line_items" />
        <Resource name="payments" {...payments} />
        <Resource name="products" {...pricebook} />
        <Resource name="appointments" />
        <Resource name="booking_pages" />
        <Resource name="sequences" />
        <Resource name="sequence_steps" />
        <Resource name="sequence_enrollments" />
        <Resource name="forms" />
        <Resource name="form_submissions" />
        <Resource name="review_requests" />
        <Resource name="segments" />
        <Resource name="campaigns" />
        <Resource name="campaign_recipients" />
        <Resource name="workflows" />
        <Resource name="workflow_actions" />
        <Resource name="workflow_runs" />
        <Resource name="workflow_run_steps" />
        <Resource name="calls" />
        <Resource name="record_files" />
        <Resource name="comments" />
        <Resource name="notifications" />
        <Resource name="message_templates" />
        <Resource name="contact_timeline" />
        <Resource name="conversations" />
        <Resource name="messages" />
        <Resource name="integration_connections" />
        <Resource name="workspaces" />
        <Resource name="contact_notes" />
        <Resource name="deal_notes" />
        <Resource name="sales" {...sales} />
        <Resource name="tags" />
      </Admin>
    </PersistQueryClientProvider>
  );
};
